import { useEffect, useState } from 'react'
import { supabase } from '../../../../lib/supabase'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

export default function BrandingForm() {
  const [club, setClub] = useState(null)
  const [formData, setFormData] = useState({ primary_color: '#16a34a', secondary_color: '#ffffff' })
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)
  const [logoUrl, setLogoUrl] = useState(null)

  useEffect(() => {
    const fetchClub = async () => {
      const { data } = await supabase.from('clubs').select('name, primary_color, secondary_color, logo_url').eq('id', CLUB_ID).single()
      if (data) {
        setClub(data)
        setFormData({ primary_color: data.primary_color || '#16a34a', secondary_color: data.secondary_color || '#ffffff' })
        setLogoUrl(data.logo_url || null)
      }
    }
    fetchClub()
  }, [])

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return }
    if (file.size > 2 * 1024 * 1024) { setError('Image must be under 2MB'); return }
    setUploading(true)
    setError(null)
    const fileExt = file.name.split('.').pop()
    const fileName = `${CLUB_ID}-logo.${fileExt}`
    const { error: uploadError } = await supabase.storage.from('club-logos').upload(fileName, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('club-logos').getPublicUrl(fileName)
    const { error: updateError } = await supabase.from('clubs').update({ logo_url: publicUrl }).eq('id', CLUB_ID)
    if (updateError) { setError(updateError.message); setUploading(false); return }
    setLogoUrl(publicUrl)
    setUploading(false)
    setSuccess(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setSuccess(false)
    setError(null)
    const { error } = await supabase.from('clubs').update(formData).eq('id', CLUB_ID)
    if (error) { setError(error.message) } else { setSuccess(true) }
    setSaving(false)
  }

  const labelClass = "text-xs text-gray-500 uppercase tracking-widest font-semibold"

  return (
    <div className="bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 p-5 sm:p-8">
      <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-6">Club Branding</div>

      {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 text-green-600 dark:text-green-400 px-4 py-3 text-sm mb-4">Branding updated successfully</div>}
      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400 px-4 py-3 text-sm mb-4">{error}</div>}

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-lg">

        {/* Logo Upload */}
        <div className="flex flex-col gap-3">
          <label className={labelClass}>Club Logo</label>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
            <div className="w-20 h-20 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center shrink-0">
              {logoUrl ? (
                <img src={logoUrl} alt="Club logo" className="w-full h-full object-contain p-2" />
              ) : (
                <div className="text-gray-400 text-xs uppercase tracking-widest text-center px-2">No Logo</div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="bg-gray-200 dark:bg-white/10 hover:bg-gray-300 dark:hover:bg-white/20 text-gray-700 dark:text-white px-4 py-2 text-xs font-bold uppercase tracking-widest cursor-pointer transition-colors w-fit">
                {uploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
                <input type="file" accept="image/*" onChange={handleLogoUpload} disabled={uploading} className="hidden" />
              </label>
              <p className="text-gray-400 text-xs">PNG or SVG recommended — max 2MB</p>
              <p className="text-gray-400 text-xs">Logo appears in the admin sidebar and member cards</p>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4 border-t border-gray-200 dark:border-white/10">
          <div className="flex flex-col gap-3">
            <label className={labelClass}>Primary Color</label>
            <div className="flex items-center gap-3">
              <input type="color" value={formData.primary_color} onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })} className="w-12 h-12 bg-transparent border border-gray-200 dark:border-white/10 cursor-pointer" />
              <div className="text-gray-500 text-sm font-mono">{formData.primary_color}</div>
            </div>
            <div className="h-10 border border-gray-200 dark:border-white/10" style={{ background: formData.primary_color }} />
          </div>
          <div className="flex flex-col gap-3">
            <label className={labelClass}>Secondary Color</label>
            <div className="flex items-center gap-3">
              <input type="color" value={formData.secondary_color} onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })} className="w-12 h-12 bg-transparent border border-gray-200 dark:border-white/10 cursor-pointer" />
              <div className="text-gray-500 text-sm font-mono">{formData.secondary_color}</div>
            </div>
            <div className="h-10 border border-gray-200 dark:border-white/10" style={{ background: formData.secondary_color }} />
          </div>
        </div>

        {/* Preview */}
        <div className="border-t border-gray-200 dark:border-white/10 pt-6">
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">Preview</div>
          <div className="p-4 sm:p-6 flex items-center justify-between" style={{ background: formData.primary_color }}>
            <div className="flex items-center gap-3 min-w-0">
              {logoUrl && <img src={logoUrl} alt="Logo" className="w-8 h-8 object-contain shrink-0" />}
              <span className="font-black text-base sm:text-xl uppercase tracking-widest truncate" style={{ color: formData.secondary_color }}>
                {club?.name || 'Club Name'}
              </span>
            </div>
            <span className="text-xs font-bold uppercase tracking-widest px-3 sm:px-4 py-2 border shrink-0 ml-3" style={{ color: formData.primary_color, background: formData.secondary_color, borderColor: formData.secondary_color }}>
              DSMS
            </span>
          </div>
        </div>

        <button type="submit" disabled={saving} className="bg-green-600 hover:bg-green-700 disabled:bg-green-900 text-white py-3 font-bold uppercase tracking-widest text-sm transition-colors w-fit px-8">
          {saving ? 'Saving...' : 'Save Branding'}
        </button>
      </form>
    </div>
  )
}